import _ from 'lodash';
import { SetterOrUpdater } from 'recoil';
import IApiCaller from '../types/api-caller';
import { ICompanySummary, IListStatistics, IUserListRef } from '../types/company';
import { getDowngradeBucket } from '../utils/downgrade-utilities';
import { toast } from 'react-toastify';
import { getCompanyListIds } from './compare-functions';

const filterCompaniesByUserList = (userListId: number, companies: ICompanySummary[]): ICompanySummary[] => {
  return companies.filter((company) => getCompanyListIds(company).includes(userListId));
};

const getNumberOfCompanies = (filteredCompanies: ICompanySummary[]): number => {
  return _.size(filteredCompanies);
};

// exclude score that are exactly 0 from the average
const getAverageScore = (filteredCompanies: ICompanySummary[]): number => {
  const withoutNoArticles = _.filter(filteredCompanies, (company) => company.current_score !== 0);
  if (_.size(withoutNoArticles) === 0) {
    return 0;
  }

  return Math.round(_.meanBy(withoutNoArticles, 'current_score'));
};

const getDailyPercentChange = (filteredCompanies: ICompanySummary[]): number => {
  return Math.round(_.meanBy(filteredCompanies, 'previous_day_percent_change') * 10) / 10;
};

const getWeeklyPercentChange = (filteredCompanies: ICompanySummary[]): number => {
  return Math.round(_.meanBy(filteredCompanies, 'previous_week_percent_change') * 10) / 10;
};

export const createUserListStats = (userLists: IUserListRef[], companies: ICompanySummary[]): IListStatistics[] => {
  const userListsById = userLists.reduce<Record<number, IUserListRef>>((acc, list) => {
    acc[list.id] = list;
    return acc;
  }, {});

  const userListIds = _.orderBy(
    _.uniqBy(userLists, 'id').map((list) => list.id),
    [(id) => _.toLower(userListsById[id]?.name ?? '')],
    ['asc']
  );

  return userListIds.map((listId) => {
    const filteredCompanies = filterCompaniesByUserList(listId, companies);
    const current_score = getAverageScore(filteredCompanies);
    const bucket = getDowngradeBucket(current_score / 100.0);

    return {
      listId,
      listName: userListsById[listId]?.name ?? `List ${listId}`,
      numberOfCompanies: getNumberOfCompanies(filteredCompanies),
      current_score,
      current_score_color_name: bucket.color,
      current_score_sort_order: bucket.sortOrder,
      current_score_text: bucket.text,
      previous_day_percent_change: getDailyPercentChange(filteredCompanies),
      previous_week_percent_change: getWeeklyPercentChange(filteredCompanies),
    };
  });
};

export const createUserListAndUpdateState = async ({
  apiCaller,
  newListName,
  companyCode,
  setUserLists,
  setCompanies,
}: {
  apiCaller: IApiCaller;
  newListName: string;
  companyCode: string;
  setUserLists: SetterOrUpdater<IUserListRef[]>;
  setCompanies: SetterOrUpdater<ICompanySummary[]>;
}): Promise<{ user_list_id: number; list_name: string }> => {
  const cleanedName = newListName.trim().replace(/\s+/g, ' ').replace(/"/g, '').replace(/,/g, '');
  if (!cleanedName) {
    throw new Error('List name is empty after cleanup');
  }

  try {
    const res = await apiCaller.createUserList({ list_name: cleanedName, company_code: companyCode });

    if (typeof res.user_list_id !== 'number') {
      throw new Error('createUserList did not return a valid list id');
    }

    const newId: number = res.user_list_id;
    const finalName: string = res.list_name ?? cleanedName;

    setUserLists((prev) =>
      _.orderBy(_.uniqBy([...(prev ?? []), { id: newId, name: finalName }], 'id'), [(list) => _.toLower(list.name)], ['asc'])
    );

    setCompanies((prevCompanies) =>
      prevCompanies.map((c) => {
        if (c.company_code !== companyCode) {
          return c;
        }

        const nextLists = _.uniqBy([...(c.lists ?? []), { id: newId, name: finalName }], 'id');
        return { ...c, lists: nextLists };
      })
    );

    return { user_list_id: newId, list_name: finalName };
  } catch (err: any) {
    console.error(err);
    toast.error(`Could not create list "${cleanedName}"`);
    throw err;
  }
};

export const onUserListAssignmentChange = async (
  companies: ICompanySummary[],
  setCompanies: SetterOrUpdater<ICompanySummary[]>,
  apiCaller: IApiCaller,
  userLists: IUserListRef[],
  companyCode: string,
  companyName: string,
  nextListIds: number[]
): Promise<void> => {
  const localCompanies = [...companies];
  const index = localCompanies.findIndex((company) => company.company_code === companyCode);

  if (index === -1) return;

  const prevCompany = localCompanies[index];
  const prevListIds = getCompanyListIds(prevCompany);
  const userListsById = userLists.reduce<Record<number, IUserListRef>>((acc, list) => {
    acc[list.id] = list;
    return acc;
  }, {});

  const normalisedNext = _.uniq(nextListIds).filter((id) => typeof id === 'number' && id > 0);
  const normalisedPrev = _.uniq(prevListIds).filter((id) => typeof id === 'number' && id > 0);

  const optimisticCompany: ICompanySummary = {
    ...prevCompany,
    lists: normalisedNext.map((id) => userListsById[id] ?? { id: id, name: `List ${id}` }),
  };

  localCompanies[index] = optimisticCompany;
  setCompanies(localCompanies);

  // diff: which listIds to add/remove
  const toAdd = normalisedNext.filter((id) => !normalisedPrev.includes(id));
  const toRemove = normalisedPrev.filter((id) => !normalisedNext.includes(id));

  // Apply changes
  try {
    await Promise.all([
      ...toAdd.map((id) => apiCaller.addCompanyToList({ id, company_code: companyCode })),
      ...toRemove.map((id) => apiCaller.removeCompanyFromList({ id, company_code: companyCode })),
    ]);
  } catch (err: any) {
    const rollbackCompanies = [...companies];
    const rollbackIndex = rollbackCompanies.findIndex((c) => c.company_code === companyCode);
    if (rollbackIndex !== -1) {
      rollbackCompanies[rollbackIndex] = {
        ...rollbackCompanies[rollbackIndex],
        lists: prevCompany.lists,
      };
      setCompanies(rollbackCompanies);
    }

    const msg = `Did not save list changes for ${companyName}`;
    console.error(err, msg);
    toast.error(msg);
  }
};

export const onUserListDeletion = async ({
  listId,
  companies,
  setCompanies,
  setUserLists,
  apiCaller,
}: {
  listId: number;
  companies: ICompanySummary[];
  setCompanies: SetterOrUpdater<ICompanySummary[]>;
  setUserLists: SetterOrUpdater<IUserListRef[]>;
  apiCaller: IApiCaller;
}): Promise<void> => {
  if (!Number.isFinite(listId)) return;

  try {
    await apiCaller.deleteUserList({ id: listId });

    setCompanies(
      companies.map((c) => ({
        ...c,
        lists: (c.lists ?? []).filter((list) => list.id !== listId),
      }))
    );

    setUserLists((prev) => (prev ?? []).filter((list) => list.id !== listId));
  } catch (err: any) {
    console.error(err);
    toast.error('Could not delete list. Please try again.');
  }
};
